import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabaseClient';
import AdminLayout from '../../components/admin/AdminLayout';
import {
  Users,
  CheckCircle,
  XCircle,
  Clock,
  Filter,
  Search,
  ChevronDown,
  ChevronUp,
  Key,
  Copy,
  Eye,
  EyeOff,
  RefreshCw,
  Building2,
  User,
  Mail,
  AlertTriangle,
  X,
  Send,
  Minus
} from 'lucide-react';

interface UserAccountRequest {
  id: string;
  application_name: string;
  requestor_name: string;
  company_name: string;
  user_role: string;
  request_type: string;
  request_details: string;
  with_billing: boolean;
  invited_first_name: string;
  invited_last_name: string;
  invited_title: string;
  invited_email: string;
  invited_department: string;
  invited_role: string;
  status: string;
  created_at: string;
  updated_at: string;
  workday_customer_id: string | null;
  rejection_reason?: string;
  closed_by_client_at?: string | null;
}

interface GroupedRequests {
  ownerName: string;
  companyName: string;
  customerId: string | null;
  requests: UserAccountRequest[];
  ownerPlan?: string;
  totalSeats?: number;
  seatsUsed?: number;
}

export default function UserAccountRequests() {
  const [requests, setRequests] = useState<UserAccountRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [expandedRequest, setExpandedRequest] = useState<string | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);

  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<UserAccountRequest | null>(null);
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [copying, setCopying] = useState(false);
  const [grantProJobAccess, setGrantProJobAccess] = useState(false);
  const [ownerProfiles, setOwnerProfiles] = useState<Record<string, { plan: string; total_seats: number }>>({});

  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectingRequest, setRejectingRequest] = useState<UserAccountRequest | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [sendingRejection, setSendingRejection] = useState(false);

  useEffect(() => {
    fetchRequests();

    const channel = supabase
      .channel('user_account_requests_admin')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'user_account_requests' }, fetchRequests)
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, []);

  const fetchRequests = async () => {
    try {
      const { data, error } = await supabase
        .from('user_account_requests')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setRequests(data || []);

      // Fetch owner plan/seats for all unique customer IDs
      const customerIds = [...new Set((data || []).map(r => r.workday_customer_id).filter(Boolean))] as string[];
      if (customerIds.length > 0) {
        const { data: profiles } = await supabase
          .from('workday_client_profiles')
          .select('id, plan, total_seats')
          .in('id', customerIds);
        if (profiles) {
          const map: Record<string, { plan: string; total_seats: number }> = {};
          profiles.forEach(p => { map[p.id] = { plan: p.plan || 'free', total_seats: p.total_seats || 2 }; });
          setOwnerProfiles(map);
        }
      }
    } catch (error) {
      console.error('Error fetching requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const generatePassword = () => {
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
    return Array.from({ length: 12 }, () => charset[Math.floor(Math.random() * charset.length)]).join('');
  };

  const generateVerificationCode = () => Math.floor(100000 + Math.random() * 900000).toString();

  const handleApproveWithPassword = (request: UserAccountRequest) => {
    setSelectedRequest(request);
    setGeneratedPassword(generatePassword());
    setVerificationCode(generateVerificationCode());
    setGrantProJobAccess(false);
    setShowPasswordModal(true);
  };

  const handleCopyPassword = async () => {
    try {
      await navigator.clipboard.writeText(generatedPassword);
      setCopying(true);
      setTimeout(() => setCopying(false), 2000);
    } catch (err) {
      console.error('Failed to copy password:', err);
    }
  };

  const confirmApproval = async () => {
    if (!selectedRequest) return;
    setUpdatingStatus(selectedRequest.id);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('No active session');

      const isTeamInvite = selectedRequest.request_type === 'team_member_access' && selectedRequest.invited_email;

      if (isTeamInvite) {
        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-approve-invite`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${session.access_token}`,
              'Content-Type': 'application/json',
              Apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
            },
            body: JSON.stringify({
              requestId: selectedRequest.id,
              invitedEmail: selectedRequest.invited_email,
              invitedFirstName: selectedRequest.invited_first_name,
              invitedLastName: selectedRequest.invited_last_name,
              invitedTitle: selectedRequest.invited_title || '',
              invitedDepartment: selectedRequest.invited_department || '',
              invitedRole: selectedRequest.invited_role || 'team_member',
              generatedPassword,
              verificationCode,
              workdayCustomerId: selectedRequest.workday_customer_id || '',
              hasProJobAccess: grantProJobAccess,
            }),
          }
        );
        const result = await response.json();
        if (!response.ok) throw new Error(result.error || 'Failed to approve invitation');
      } else {
        const { data: { user } } = await supabase.auth.getUser();
        const { error: messageError } = await supabase.from('credential_messages').insert({
          user_account_request_id: selectedRequest.id,
          verification_code: verificationCode,
          generated_password: generatedPassword,
          recipient_email: selectedRequest.invited_email,
          created_by: user?.id,
        });
        if (messageError) throw messageError;

        if (selectedRequest.status === 'pending') {
          await supabase.from('user_account_requests')
            .update({ status: 'approved', updated_at: new Date().toISOString() })
            .eq('id', selectedRequest.id);
        }
      }

      setRequests(prev => prev.map(r => r.id === selectedRequest.id
        ? { ...r, status: 'approved', updated_at: new Date().toISOString() }
        : r
      ));
      setShowPasswordModal(false);
      setSelectedRequest(null);
      setGeneratedPassword('');
      setVerificationCode('');
    } catch (error) {
      console.error('Error approving request:', error);
    } finally {
      setUpdatingStatus(null);
    }
  };

  const openRejectModal = (request: UserAccountRequest) => {
    setRejectingRequest(request);
    setRejectionReason('');
    setShowRejectModal(true);
  };

  const handleReject = async () => {
    if (!rejectingRequest || !rejectionReason.trim()) return;
    setSendingRejection(true);

    try {
      const { error } = await supabase
        .from('user_account_requests')
        .update({
          status: 'rejected',
          rejection_reason: rejectionReason.trim(),
          updated_at: new Date().toISOString(),
        })
        .eq('id', rejectingRequest.id);

      if (error) throw error;

      await supabase.from('admin_messages').insert({
        to_client_id: rejectingRequest.workday_customer_id,
        subject: `Invite User Request Update: ${rejectingRequest.invited_first_name} ${rejectingRequest.invited_last_name}`,
        message: `Your invitation request for ${rejectingRequest.invited_first_name} ${rejectingRequest.invited_last_name} (${rejectingRequest.invited_email}) has been rejected.\n\nReason: ${rejectionReason.trim()}\n\nIf you have questions, please reply to this message.`,
      });

      setRequests(prev => prev.map(r => r.id === rejectingRequest.id
        ? { ...r, status: 'rejected', rejection_reason: rejectionReason.trim(), updated_at: new Date().toISOString() }
        : r
      ));
      setShowRejectModal(false);
      setRejectingRequest(null);
      setRejectionReason('');
    } catch (error) {
      console.error('Error rejecting request:', error);
    } finally {
      setSendingRejection(false);
    }
  };

  const updateRequestStatus = async (requestId: string, newStatus: string) => {
    setUpdatingStatus(requestId);
    try {
      await supabase.from('user_account_requests')
        .update({ status: newStatus, updated_at: new Date().toISOString() })
        .eq('id', requestId);

      setRequests(prev => prev.map(r => r.id === requestId
        ? { ...r, status: newStatus, updated_at: new Date().toISOString() }
        : r
      ));
    } catch (error) {
      console.error('Error updating request status:', error);
    } finally {
      setUpdatingStatus(null);
    }
  };

  const getStatusBadge = (status: string) => {
    const configs: Record<string, { cls: string; icon: React.ReactNode; label: string }> = {
      pending: { cls: 'bg-yellow-50 text-yellow-700 border-yellow-200', icon: <Clock className="w-3 h-3" />, label: 'Pending' },
      approved: { cls: 'bg-green-50 text-green-700 border-green-200', icon: <CheckCircle className="w-3 h-3" />, label: 'Approved' },
      rejected: { cls: 'bg-red-50 text-red-700 border-red-200', icon: <XCircle className="w-3 h-3" />, label: 'Rejected' },
      closed: { cls: 'bg-gray-50 text-gray-500 border-gray-200', icon: <Minus className="w-3 h-3" />, label: 'Closed by Client' },
    };
    const cfg = configs[status] || configs.pending;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium ${cfg.cls}`}>
        {cfg.icon}{cfg.label}
      </span>
    );
  };

  const getRequestTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      team_member_access: 'Team Member Invite',
      admin_access: 'Admin Access',
      reset_password: 'Reset Password',
      billing_access: 'Billing Access',
      delete_account: 'Delete Account',
      disable_account: 'Disable Account',
      restricted_account: 'Restricted Account',
    };
    return labels[type] || type;
  };

  const filtered = requests.filter(r => {
    if (statusFilter !== 'all' && r.status !== statusFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (
        r.requestor_name.toLowerCase().includes(q) ||
        r.company_name.toLowerCase().includes(q) ||
        r.invited_first_name.toLowerCase().includes(q) ||
        r.invited_last_name.toLowerCase().includes(q) ||
        r.invited_email.toLowerCase().includes(q)
      );
    }
    return true;
  });

  const grouped: GroupedRequests[] = Object.values(
    filtered.reduce((acc, req) => {
      const key = req.workday_customer_id || req.requestor_name;
      if (!acc[key]) {
        const profile = req.workday_customer_id ? ownerProfiles[req.workday_customer_id] : undefined;
        acc[key] = {
          ownerName: req.requestor_name,
          companyName: req.company_name,
          customerId: req.workday_customer_id,
          requests: [],
          ownerPlan: profile?.plan || 'free',
          totalSeats: profile?.total_seats || 2,
        };
      }
      acc[key].requests.push(req);
      return acc;
    }, {} as Record<string, GroupedRequests>)
  ).map(group => ({
    ...group,
    seatsUsed: 1 + group.requests.filter(r => r.status === 'approved').length,
  }));

  const toggleGroup = (key: string) => {
    setExpandedGroups(prev => {
      const n = new Set(prev);
      if (n.has(key)) n.delete(key); else n.add(key);
      return n;
    });
  };

  const stats = {
    total: requests.length,
    pending: requests.filter(r => r.status === 'pending').length,
    approved: requests.filter(r => r.status === 'approved').length,
    rejected: requests.filter(r => r.status === 'rejected').length,
    closed: requests.filter(r => r.status === 'closed').length,
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6">
        <div className="max-w-7xl mx-auto">

          {/* Approve / Password Modal */}
          {showPasswordModal && selectedRequest && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-1">
                  {selectedRequest.status === 'approved' ? 'Generate New Password' : 'Approve & Generate Password'}
                </h2>
                <p className="text-gray-500 text-sm mb-5">
                  For {selectedRequest.invited_first_name} {selectedRequest.invited_last_name}
                </p>

                <div className="space-y-4 mb-6">
                  <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-2">Verification Code</label>
                    <code className="block px-4 py-3 bg-white border border-gray-200 rounded-lg text-2xl font-mono font-bold text-center tracking-widest text-blue-600">
                      {verificationCode}
                    </code>
                    <p className="text-xs text-gray-400 mt-2">Shown to the user to verify their credentials</p>
                  </div>

                  <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Generated Password</label>
                      <button onClick={() => setShowPassword(!showPassword)} className="text-gray-400 hover:text-gray-700">
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={generatedPassword}
                        readOnly
                        className="flex-1 px-3 py-2 bg-white border border-gray-200 rounded-lg font-mono text-gray-900 text-sm"
                      />
                      <button
                        onClick={handleCopyPassword}
                        className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-1.5 text-sm"
                      >
                        <Copy className="w-3.5 h-3.5" />
                        {copying ? 'Copied!' : 'Copy'}
                      </button>
                    </div>
                  </div>

                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className="text-xs text-amber-800">
                      The user will see a verification code on their invite page. Credentials expire after 24 hours.
                    </p>
                  </div>

                  {/* Pro job access toggle — shown when owner is on pro plan */}
                  {selectedRequest.workday_customer_id && ownerProfiles[selectedRequest.workday_customer_id]?.plan === 'pro' && (
                    <div className="flex items-center justify-between p-3 rounded-lg bg-teal-50 border border-teal-200">
                      <div>
                        <p className="text-sm font-semibold text-teal-900">Grant Pro Job Postings Access</p>
                        <p className="text-xs text-teal-700 mt-0.5">Allow this team member to view Pro-tier job postings</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setGrantProJobAccess(v => !v)}
                        className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${grantProJobAccess ? 'bg-teal-500' : 'bg-gray-300'}`}
                      >
                        <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${grantProJobAccess ? 'translate-x-5' : ''}`} />
                      </button>
                    </div>
                  )}
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={confirmApproval}
                    disabled={updatingStatus === selectedRequest.id}
                    className="flex-1 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 font-medium text-sm"
                  >
                    {selectedRequest.status === 'approved' ? 'Send New Credentials' : 'Confirm & Send Credentials'}
                  </button>
                  <button
                    onClick={() => { setShowPasswordModal(false); setSelectedRequest(null); }}
                    className="px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Rejection Modal */}
          {showRejectModal && rejectingRequest && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                      <AlertTriangle className="w-5 h-5 text-red-600" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-gray-900">Reject Invite Request</h2>
                      <p className="text-sm text-gray-500">
                        For {rejectingRequest.invited_first_name} {rejectingRequest.invited_last_name}
                      </p>
                    </div>
                  </div>
                  <button onClick={() => setShowRejectModal(false)} className="text-gray-400 hover:text-gray-600">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="mb-5">
                  <p className="text-sm text-gray-600 mb-4">
                    A message will automatically be sent to <strong>{rejectingRequest.requestor_name}</strong> at <strong>{rejectingRequest.company_name}</strong> with your rejection reason.
                  </p>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Reason for Rejection <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={rejectionReason}
                    onChange={e => setRejectionReason(e.target.value)}
                    placeholder="Explain why this invite request is being rejected..."
                    rows={4}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={handleReject}
                    disabled={!rejectionReason.trim() || sendingRejection}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm"
                  >
                    <Send className="w-4 h-4" />
                    {sendingRejection ? 'Sending...' : 'Reject & Notify Client'}
                  </button>
                  <button
                    onClick={() => { setShowRejectModal(false); setRejectingRequest(null); setRejectionReason(''); }}
                    className="px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Header */}
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-gray-900 mb-1">Invite User Requests</h1>
            <p className="text-gray-500 text-sm">Manage client team member invitation requests</p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            {[
              { label: 'Total', value: stats.total, cls: 'text-gray-900' },
              { label: 'Pending', value: stats.pending, cls: 'text-yellow-600' },
              { label: 'Approved', value: stats.approved, cls: 'text-green-600' },
              { label: 'Rejected', value: stats.rejected, cls: 'text-red-600' },
              { label: 'Closed by Client', value: stats.closed, cls: 'text-gray-500' },
            ].map(s => (
              <div key={s.label} className="bg-white rounded-xl border border-gray-200 p-4">
                <div className={`text-2xl font-bold ${s.cls}`}>{s.value}</div>
                <div className="text-gray-500 text-xs mt-0.5">{s.label}</div>
              </div>
            ))}
          </div>

          {/* Filters */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6 flex flex-col md:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, company, email..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="closed">Closed by Client</option>
              </select>
            </div>
          </div>

          {/* Grouped List */}
          {grouped.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
              <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No invite requests found</p>
            </div>
          ) : (
            <div className="space-y-4">
              {grouped.map(group => {
                const groupKey = group.customerId || group.ownerName;
                const isGroupOpen = expandedGroups.has(groupKey);
                const pendingCount = group.requests.filter(r => r.status === 'pending').length;

                return (
                  <div key={groupKey} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    {/* Group header - account owner + company */}
                    <button
                      onClick={() => toggleGroup(groupKey)}
                      className="w-full flex items-center gap-4 px-5 py-4 hover:bg-gray-50 transition-colors text-left"
                    >
                      <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
                        <Building2 className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-gray-900">{group.companyName || 'Unknown Company'}</span>
                          {pendingCount > 0 && (
                            <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs font-semibold rounded-full">
                              {pendingCount} pending
                            </span>
                          )}
                          <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${group.ownerPlan === 'pro' ? 'bg-teal-100 text-teal-700' : 'bg-gray-100 text-gray-500'}`}>
                            {group.ownerPlan === 'pro' ? 'PRO' : 'FREE'}
                          </span>
                          <span className="px-2 py-0.5 bg-blue-50 text-blue-600 text-xs font-medium rounded-full">
                            {group.seatsUsed}/{group.totalSeats} seats
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <User className="w-3 h-3 text-gray-400" />
                          <span className="text-sm text-gray-500">Account Owner: {group.ownerName}</span>
                          <span className="text-gray-300 mx-1">·</span>
                          <span className="text-sm text-gray-400">{group.requests.length} request{group.requests.length !== 1 ? 's' : ''}</span>
                        </div>
                      </div>
                      {isGroupOpen
                        ? <ChevronUp className="w-5 h-5 text-gray-400 flex-shrink-0" />
                        : <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                      }
                    </button>

                    {/* Requests under this owner */}
                    {isGroupOpen && (
                      <div className="border-t border-gray-100 divide-y divide-gray-100">
                        {group.requests.map(request => {
                          const isExpanded = expandedRequest === request.id;
                          return (
                            <div key={request.id} className={`${request.status === 'closed' ? 'bg-gray-50/60' : 'bg-white'}`}>
                              {/* Request row */}
                              <div
                                className="flex items-center gap-3 px-5 py-3.5 cursor-pointer hover:bg-gray-50 transition-colors"
                                onClick={() => setExpandedRequest(isExpanded ? null : request.id)}
                              >
                                <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                                  <User className="w-4 h-4 text-gray-500" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap mb-0.5">
                                    <span className="font-medium text-gray-900 text-sm">
                                      {request.invited_first_name} {request.invited_last_name}
                                    </span>
                                    {getStatusBadge(request.status)}
                                    <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                                      {getRequestTypeLabel(request.request_type)}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-3 text-xs text-gray-500">
                                    <span className="flex items-center gap-1">
                                      <Mail className="w-3 h-3" />{request.invited_email}
                                    </span>
                                    {request.invited_title && (
                                      <span>· {request.invited_title}</span>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-3 flex-shrink-0">
                                  <span className="text-xs text-gray-400">
                                    {new Date(request.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                  </span>
                                  {isExpanded
                                    ? <ChevronUp className="w-4 h-4 text-gray-400" />
                                    : <ChevronDown className="w-4 h-4 text-gray-400" />
                                  }
                                </div>
                              </div>

                              {/* Expanded request detail */}
                              {isExpanded && (
                                <div className="px-5 pb-5 pt-2 border-t border-gray-100 bg-gray-50">
                                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4 text-sm">
                                    {request.invited_role && (
                                      <div>
                                        <span className="text-xs text-gray-400 block">Requested Role</span>
                                        <span className="font-medium text-gray-900">{request.invited_role}</span>
                                      </div>
                                    )}
                                    {request.invited_department && (
                                      <div>
                                        <span className="text-xs text-gray-400 block">Department</span>
                                        <span className="font-medium text-gray-900">{request.invited_department}</span>
                                      </div>
                                    )}
                                    <div>
                                      <span className="text-xs text-gray-400 block">With Billing</span>
                                      <span className="font-medium text-gray-900">{request.with_billing ? 'Yes' : 'No'}</span>
                                    </div>
                                    {request.workday_customer_id && ownerProfiles[request.workday_customer_id] && (
                                      <>
                                        <div>
                                          <span className="text-xs text-gray-400 block">Owner Plan</span>
                                          <span className={`font-semibold text-sm px-2 py-0.5 rounded-full inline-block ${ownerProfiles[request.workday_customer_id].plan === 'pro' ? 'bg-teal-100 text-teal-700' : 'bg-gray-100 text-gray-600'}`}>
                                            {ownerProfiles[request.workday_customer_id].plan?.toUpperCase()}
                                          </span>
                                        </div>
                                        <div>
                                          <span className="text-xs text-gray-400 block">Seats (used/total)</span>
                                          <span className="font-medium text-gray-900">
                                            {1 + group.requests.filter(r => r.status === 'approved').length} / {ownerProfiles[request.workday_customer_id].total_seats}
                                          </span>
                                        </div>
                                      </>
                                    )}
                                    <div>
                                      <span className="text-xs text-gray-400 block">Submitted</span>
                                      <span className="font-medium text-gray-900">
                                        {new Date(request.created_at).toLocaleDateString()} {new Date(request.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                      </span>
                                    </div>
                                    <div>
                                      <span className="text-xs text-gray-400 block">Last Updated</span>
                                      <span className="font-medium text-gray-900">
                                        {new Date(request.updated_at).toLocaleDateString()} {new Date(request.updated_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                      </span>
                                    </div>
                                    {request.closed_by_client_at && (
                                      <div>
                                        <span className="text-xs text-gray-400 block">Closed by Client</span>
                                        <span className="font-medium text-gray-900">
                                          {new Date(request.closed_by_client_at).toLocaleDateString()}
                                        </span>
                                      </div>
                                    )}
                                  </div>

                                  {request.request_details && (
                                    <div className="mb-4">
                                      <span className="text-xs text-gray-400 block mb-1">Request Details</span>
                                      <p className="text-sm text-gray-700 bg-white border border-gray-200 rounded-lg p-3">
                                        {request.request_details}
                                      </p>
                                    </div>
                                  )}

                                  {request.rejection_reason && (
                                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                                      <span className="text-xs font-semibold text-red-600 block mb-1">Rejection Reason Sent to Client</span>
                                      <p className="text-sm text-red-800">{request.rejection_reason}</p>
                                    </div>
                                  )}

                                  {/* Action buttons */}
                                  {request.status === 'pending' && (
                                    <div className="flex gap-2 flex-wrap">
                                      <button
                                        onClick={() => handleApproveWithPassword(request)}
                                        disabled={updatingStatus === request.id}
                                        className="flex items-center gap-1.5 px-3 py-2 bg-green-50 text-green-700 border border-green-200 rounded-lg hover:bg-green-100 transition-colors disabled:opacity-50 text-sm font-medium"
                                      >
                                        <Key className="w-3.5 h-3.5" />
                                        Approve & Generate Password
                                      </button>
                                      <button
                                        onClick={() => openRejectModal(request)}
                                        disabled={updatingStatus === request.id}
                                        className="flex items-center gap-1.5 px-3 py-2 bg-red-50 text-red-700 border border-red-200 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50 text-sm font-medium"
                                      >
                                        <XCircle className="w-3.5 h-3.5" />
                                        Reject
                                      </button>
                                    </div>
                                  )}

                                  {request.status === 'approved' && request.invited_email && (
                                    <div className="flex gap-2 flex-wrap">
                                      <button
                                        onClick={() => handleApproveWithPassword(request)}
                                        disabled={updatingStatus === request.id}
                                        className="flex items-center gap-1.5 px-3 py-2 bg-blue-50 text-blue-700 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors disabled:opacity-50 text-sm font-medium"
                                      >
                                        <RefreshCw className="w-3.5 h-3.5" />
                                        Generate New Password
                                      </button>
                                      <button
                                        onClick={() => openRejectModal(request)}
                                        disabled={updatingStatus === request.id}
                                        className="flex items-center gap-1.5 px-3 py-2 bg-red-50 text-red-700 border border-red-200 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50 text-sm font-medium"
                                      >
                                        <XCircle className="w-3.5 h-3.5" />
                                        Revoke
                                      </button>
                                    </div>
                                  )}

                                  {request.status === 'rejected' && (
                                    <button
                                      onClick={() => updateRequestStatus(request.id, 'pending')}
                                      disabled={updatingStatus === request.id}
                                      className="flex items-center gap-1.5 px-3 py-2 bg-gray-100 text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 text-sm font-medium"
                                    >
                                      <RefreshCw className="w-3.5 h-3.5" />
                                      Reset to Pending
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
